import React, { useCallback, useEffect, useState, useContext } from 'react';
import {
  Box,
  BoxProps,
  chakra,
  Flex,
  HStack,
  Text,
  useColorModeValue,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
  useDisclosure
} from '@chakra-ui/react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';

import { userAtom, navbarColorAtom } from 'src/atoms';
import { useApiHelpers } from 'src/hooks/useApiHelpers';
import { UserCredit } from '../icons/UserCredit';
import ChartaAccented from './ChartaAccented';
import WMLogo from './WMLogo';
import { Menu as MenuIcon } from '../icons/Menu';
import { logoutUser } from 'src/api';
import { DemoVideoContext } from '../../providers/demoVideoProvider';
import { universalTheme } from 'src/theme/theme';
import { AlertModal } from './AlertModal';

export interface NavBarProps extends BoxProps {}

export const NavBar: React.FC<NavBarProps> = ({ ...props }) => {
  // const { toggleColorMode } = useColorMode();
  const { showLoading, showServerError, hideLoading } = useApiHelpers();
  const [user, setUser] = useAtom(userAtom);
  const navbarColor = universalTheme.background;
  const [credits, setCredits] = useState<number>(0);
  const bg = useColorModeValue('#6B30BA', 'rgba(27, 28, 49, 0.8)');
  const navigate = useNavigate();
  const { showVideo } = useContext(DemoVideoContext);
  const location = useLocation();
  const {
    isOpen: isConfirmationOpen,
    onClose: onCloseConfirmation,
    onOpen: onOpenConfirmation
  } = useDisclosure();
  const [confirmationDestination, setConfirmationDestination] =
    useState<string>('');
  useEffect(() => {
    if (!user) return;
    try {
      showLoading();
    } catch {
      hideLoading();
      showServerError();
    } finally {
      hideLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const logout = async () => {
    setUser(null);
    try {
      await logoutUser();
    } catch {
    } finally {
      navigate('/login');
    }
  };
  const isInPipelinePanel = location.pathname.includes('pipeline');

  const checkBeforeNavigatingHome = (destination: string | null = null) => {
    if (isInPipelinePanel) {
      onOpenConfirmation();
      if (destination != null) {
        setConfirmationDestination(destination);
      }
    }
  };
  return (
    <chakra.header
      h="64px"
      bg={bg}
      py="12px"
      px="32px"
      background={navbarColor}
      marginLeft={location.pathname.includes('/dashboard') ? 50 : 0}
      {...props}
    >
      <AlertModal
        isOpen={isConfirmationOpen}
        onAccept={() => {
          navigate(confirmationDestination);
          onCloseConfirmation();
          setConfirmationDestination('');
        }}
        onDecline={onCloseConfirmation}
        acceptText="Continue"
        declineText="Cancel"
        title="Are you sure you want to leave this page?"
        text="If you've saved your changes already, just press Continue. If you haven't saved your changes yet, just press Cancel and then Save Updates"
        variant="warning"
      />
      <Flex alignItems="center" justifyContent="space-between">
        <Box w={{ base: '92px', md: '167px' }} h={{ base: '24px', md: '40px' }}>
          {' '}
          {!location.pathname.includes('login') &&
            !location.pathname.includes('register') && (
              <>
                <Link
                  to={!isInPipelinePanel ? '/' : '#'}
                  onClick={() => checkBeforeNavigatingHome()}
                >
                  <img
                    style={{
                      maxHeight: '100%',
                      display: 'block',
                      margin: '0px auto'
                    }}
                    src={`${process.env.REACT_APP_API_URL}/admin/environment-logo`}
                  />
                </Link>
              </>
            )}
        </Box>

        <HStack spacing="20px" as="nav">
          {/* <Switch size="sm" onChange={() => toggleColorMode()} /> */}
          {!!user && (
            <>
              <Flex align="center" justify="space-between">
                {/* <UserCredit />
                <Text
                  pl="4px"
                  fontSize="12px"
                  fontWeight="600"
                  lineHeight="14px"
                  color="#FFC700"
                >
                  {credits} Credits
                </Text> */}
              </Flex>
              <Flex justifyContent="flex-end" alignItems="center">
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<MenuIcon height="8px" width="21.3px" />}
                    variant="ghost"
                  />
                  <MenuList>
                    <MenuItem>
                      <NavLink
                        to={!isInPipelinePanel ? '/dashboard' : '#'}
                        onClick={() => checkBeforeNavigatingHome('/dashboard')}
                        end
                      >
                        Dashboard
                      </NavLink>
                    </MenuItem>

                    <MenuItem>
                      <NavLink
                        to={!isInPipelinePanel ? '/dashboard/settings' : '#'}
                        onClick={() =>
                          checkBeforeNavigatingHome('/dashboard/settings')
                        }
                      >
                        Account Settings
                      </NavLink>
                    </MenuItem>

                    <MenuItem onClick={() => logout()}>Sign Out</MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </>
          )}
        </HStack>
      </Flex>
    </chakra.header>
  );
};

//TODO: create mobile version of this: https://levelup.gitconnected.com/create-a-responsive-navigation-bar-using-chakraui-6489473e933
